import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkle, ArrowRight } from "@phosphor-icons/react";
import { GoogleButton } from "@/components/GoogleButton";
import { LogoMark } from "@/components/Logo";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: "", password: "", full_name: "", company_name: "",
  });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!acceptTerms) {
      toast.error("Debes aceptar los Terminos y la Politica de Privacidad");
      return;
    }
    setLoading(true);
    try {
      const u = await register({ ...form, accept_terms: true });
      toast.success("Cuenta creada");
      navigate(u?.is_super_admin ? "/admin-panel" : "/app/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al registrar");
    } finally {
      setLoading(false);
    }
  };

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#09090B] text-[#FAFAFA] bg-grid p-6">
      <div className="w-full max-w-md fade-in">
        <Link to="/" className="flex items-center gap-2.5 mb-10 justify-center">
          <LogoMark size={40} />
          <div className="font-display font-medium text-lg tracking-tight">
            <span className="text-[#5E6AD2]">Wazco</span> <span className="text-[#F97316]">Express</span>
          </div>
        </Link>
        <div className="bg-[#121214] border border-[#27272A] rounded-lg p-8">
          <div className="mb-8">
            <div className="text-xs uppercase tracking-[0.2em] text-[#71717A] mb-2">Crear cuenta</div>
            <h2 className="font-display text-3xl font-medium tracking-tight">Comienza gratis</h2>
            <p className="text-[#A1A1AA] text-sm mt-1.5">Automatiza tus ventas en WhatsApp en minutos.</p>
          </div>
          <form onSubmit={submit} className="space-y-4" data-testid="register-form">
            <div>
              <Label htmlFor="full_name" className="text-xs uppercase tracking-wider text-[#71717A]">Tu nombre</Label>
              <Input
                id="full_name" value={form.full_name} onChange={change("full_name")} required
                data-testid="register-fullname-input"
                className="mt-2 bg-[#0A0A0C] border-[#27272A] focus:border-[#5E6AD2] focus:ring-1 focus:ring-[#5E6AD2] h-11"
              />
            </div>
            <div>
              <Label htmlFor="company_name" className="text-xs uppercase tracking-wider text-[#71717A]">Empresa</Label>
              <Input
                id="company_name" value={form.company_name} onChange={change("company_name")} required
                data-testid="register-company-input"
                className="mt-2 bg-[#0A0A0C] border-[#27272A] focus:border-[#5E6AD2] focus:ring-1 focus:ring-[#5E6AD2] h-11"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-[#71717A]">Email</Label>
              <Input
                id="email" type="email" value={form.email} onChange={change("email")} required
                data-testid="register-email-input"
                className="mt-2 bg-[#0A0A0C] border-[#27272A] focus:border-[#5E6AD2] focus:ring-1 focus:ring-[#5E6AD2] h-11"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-[#71717A]">Contrasena</Label>
              <Input
                id="password" type="password" value={form.password} onChange={change("password")} required minLength={6}
                data-testid="register-password-input"
                className="mt-2 bg-[#0A0A0C] border-[#27272A] focus:border-[#5E6AD2] focus:ring-1 focus:ring-[#5E6AD2] h-11"
              />
            </div>
            <label
              className="flex items-start gap-2.5 cursor-pointer select-none"
              data-testid="register-terms-label"
            >
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                required
                data-testid="register-terms-checkbox"
                className="mt-0.5 h-4 w-4 rounded border-[#3F3F46] bg-[#0A0A0C] text-[#5E6AD2] focus:ring-[#5E6AD2] focus:ring-offset-0"
              />
              <span className="text-[12px] text-[#A1A1AA] leading-snug">
                Acepto los{" "}
                <a href="/legal/terminos.pdf" target="_blank" rel="noopener noreferrer" className="text-[#5E6AD2] hover:underline" data-testid="link-terms-pdf">
                  Terminos y Condiciones
                </a>{" "}
                y la{" "}
                <a href="/legal/privacidad.pdf" target="_blank" rel="noopener noreferrer" className="text-[#F97316] hover:underline" data-testid="link-privacy-pdf">
                  Politica de Privacidad
                </a>.
              </span>
            </label>
            <Button
              type="submit" disabled={loading || !acceptTerms}
              data-testid="register-submit-button"
              className="w-full h-11 bg-[#5E6AD2] hover:bg-[#4B55C3] text-white disabled:opacity-40"
            >
              {loading ? "Creando..." : (<><span>Crear cuenta</span><ArrowRight size={16} className="ml-2" /></>)}
            </Button>
          </form>
          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-[#27272A]" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">o</span>
            <div className="flex-1 h-px bg-[#27272A]" />
          </div>
          <GoogleButton label="Continuar con Google" />
          <div className="mt-6 text-sm text-[#A1A1AA] text-center">
            Ya tienes cuenta? <Link to="/login" className="text-[#5E6AD2] hover:underline" data-testid="link-to-login">Iniciar sesion</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
